import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, patch, del } from '@/lib/apiClient';
import { API_URL } from '@/_config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CampaignGoal {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

export interface CampaignSegment {
  id: string;
  name: string;
  lead_count: number;
}

export interface AdditionalFilter {
  id: string;
  field: string;
  operator: 'is' | 'is_not' | 'contains' | 'starts_with';
  value: string;
}

export interface Campaign {
  id: string;
  campaign_id: string;
  name: string;
  status: 'active' | 'paused' | 'completed' | 'draft' | 'deleted';
  segment_id: string | null;
  segment: CampaignSegment | null;
  goal_id: string | null;
  goal: CampaignGoal | null;
  sender_profile_id: string | null;
  sender_profile_ids: string[];
  sender_profile: { id: string; from_name: string; from_email: string; is_default: boolean } | null;
  schedule_type: 'immediately' | 'later';
  scheduled_at: string | null;
  timezone: string | null;
  sending_days: string[] | null;
  sending_hours_start: string | null;
  sending_hours_end: string | null;
  daily_limit: number | null;
  additional_filters: AdditionalFilter[] | null;
  draft_step: number | null;
  draft_data: Record<string, unknown> | null;
  sent: number;
  delivered: number;
  replies: number;
  clicks: number;
  opens: number;
  seen: number;
  workflows?: Array<{ id: string; title: string }>;
  created_at: string;
  updated_at: string;
}

export interface CampaignListResponse {
  campaigns: Campaign[];
  counts: { all: number; active: number; paused: number; completed: number; draft: number };
}

export interface CampaignListParams {
  tab?: string;
  search?: string;
  sort_by?: string;
  sort_order?: string;
  page?: number;
  per_page?: number;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useCampaignsQuery(params: CampaignListParams) {
  return useQuery({
    queryKey: ['campaigns', params],
    queryFn: () => get<CampaignListResponse>(API_URL.CAMPAIGNS.LIST, params as Record<string, unknown>),
    placeholderData: (prev) => prev,
  });
}

export function useCampaignQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: () => get<Campaign>(API_URL.CAMPAIGNS.GET(id!)),
    enabled: !!id,
  });
}

export interface CampaignLead {
  id: string;
  name: string;
  company: string;
  title: string;
  email?: string | null;
  status?: string | null;
}

export function useCampaignLeadsQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign-leads', id],
    queryFn: () => get<{ leads: CampaignLead[]; total: number }>(API_URL.CAMPAIGNS.LEADS(id!)),
    enabled: !!id,
  });
}

export function useCampaignGoalsQuery() {
  return useQuery({
    queryKey: ['campaign-goals'],
    queryFn: () => get<CampaignGoal[]>(API_URL.CAMPAIGNS.GOALS),
  });
}

// ---------------------------------------------------------------------------
// Campaign Detail — analytics / contacts / emails / activity
// ---------------------------------------------------------------------------

export interface CampaignAnalytics {
  sent: number;
  delivered: number;
  bounced: number;
  failed: number;
  opened: number;
  replied: number;
  clicked: number;
  seen: number;
  enrolled: number;
  segment_count: number;
  leads_count: number;
  time_series: { date: string; sent: number; opened: number; clicked: number }[];
}

export interface CampaignContact {
  lead_id: string | null;
  email: string;
  name: string | null;
  company: string | null;
  title: string | null;
  opened: boolean;
  replied: boolean;
  opened_at: string | null;
  replied_at: string | null;
  first_contacted_at: string;
}

export interface CampaignEmailVariant {
  id: string;
  variant_label: string;
  subject_line: string | null;
  sent: number;
  opened: number;
  clicked: number;
  seen: number;
  replied: number;
}

export interface CampaignEmailStep {
  id: string;
  step_number: number;
  step_label: string;
  variants: CampaignEmailVariant[];
}

export interface CampaignEmailSequence {
  id: string;
  name: string;
  segment_name: string | null;
  workflow_name: string | null;
  steps: CampaignEmailStep[];
}

export interface CampaignActivity {
  type: 'sent' | 'scheduled' | 'replied' | 'opened' | 'bounced';
  contact_name: string | null;
  contact_count: number | null;
  step_number: number | null;
  step_label: string;
  occurred_at: string;
}

export interface CampaignActivityResponse {
  activities: CampaignActivity[];
  total: number;
  page: number;
  per_page: number;
  last_page: number;
}

export function useCampaignAnalyticsQuery(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['campaign-analytics', id],
    queryFn: () => get<CampaignAnalytics>(API_URL.CAMPAIGNS.ANALYTICS(id!)),
    enabled: !!id && enabled,
  });
}

export function useCampaignContactsQuery(
  id: string | undefined,
  enabled: boolean,
  params: { page?: number; perPage?: number; search?: string; status?: string } = {},
) {
  const { page = 1, perPage = 25, search = '', status = '' } = params;
  return useQuery({
    queryKey: ['campaign-contacts', id, page, perPage, search, status],
    queryFn: () => get<{ contacts: CampaignContact[] }>(API_URL.CAMPAIGNS.CONTACTS(id!), {
      page,
      per_page: perPage,
      ...(search && { search }),
      ...(status && { status }),
    }),
    enabled: !!id && enabled,
    placeholderData: (prev) => prev,
  });
}

export function useCampaignEmailsQuery(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['campaign-emails', id],
    queryFn: () => get<{ sequences: CampaignEmailSequence[] }>(API_URL.CAMPAIGNS.EMAILS(id!)),
    enabled: !!id && enabled,
    placeholderData: (prev) => prev,
  });
}

export function useCampaignActivityQuery(
  id: string | undefined,
  enabled: boolean,
  page: number = 1,
  perPage: number = 20,
) {
  return useQuery({
    queryKey: ['campaign-activity', id, page, perPage],
    queryFn: () =>
      get<CampaignActivityResponse>(API_URL.CAMPAIGNS.ACTIVITY(id!), {
        page,
        per_page: perPage,
      }),
    enabled: !!id && enabled,
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCreateCampaignMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      post<Campaign>(API_URL.CAMPAIGNS.CREATE, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

export function useUpdateCampaignMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      put<Campaign>(API_URL.CAMPAIGNS.UPDATE(id), data),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', variables.id] });
    },
  });
}

export function useSaveDraftMutation() {
  return useMutation({
    mutationFn: ({ id, draft_step, draft_data }: { id: string; draft_step: number; draft_data: Record<string, unknown> }) =>
      patch(API_URL.CAMPAIGNS.DRAFT(id), { draft_step, draft_data }),
  });
}

export function useUpdateStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      patch(API_URL.CAMPAIGNS.STATUS(id), { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

export function useDuplicateCampaignMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      post(API_URL.CAMPAIGNS.DUPLICATE(id), {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

export function useDeleteCampaignMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(API_URL.CAMPAIGNS.DELETE(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}
